import { useQuery, useReactiveVar } from '@apollo/client';
import { useMemo, useState } from 'react';
import { shoppingCartOffersVar } from '../apollo/reactive-variables/shopping-cart';
import INVOICE_WITH_DOCUMENT from '../graphql/queries/invoice-with-document';
import { getCurrentCredentialProvider } from '../helpers/profile/credentials';
import useSelectedCompany from './useSelectedCompany';
import DOCUMENT_TYPE_CODES from '../constants/document-type-codes';

const useOfferCartVariables = () => {
  const company = useSelectedCompany();
  const hasDigitalCertificate = company?.hasDigitalCertificate;
  const currentProvider = useMemo(
    () => getCurrentCredentialProvider(company),
    [company],
  );
  const [selectedProvider, setSelectedProvider] = useState(currentProvider);
  const [selectedCessionType, setSelectedCessionType] = useState('internal');
  const shoppingCartOffers = useReactiveVar(shoppingCartOffersVar);
  const invoiceIds = useMemo(
    () => shoppingCartOffers.map((offer) => offer.invoiceId),
    [shoppingCartOffers],
  );

  const { data, loading } = useQuery(INVOICE_WITH_DOCUMENT, {
    variables: { id_In: invoiceIds },
    skip: invoiceIds?.length === 0,
  });

  const invoices = useMemo(
    () => data?.invoices.edges.map((edge) => {
      const invoice = edge.node;
      const hasXMLDocument = invoice.documents.some(
        (doc) => doc.type.code === DOCUMENT_TYPE_CODES.XML,
      );
      return { ...invoice, hasXMLDocument };
    }) || [],
    [data],
  );

  const allDocumentsHaveXML = useMemo(
    () => invoices.every((invoice) => invoice.hasXMLDocument),
    [invoices],
  );

  const { invoiceProviderCredentials = [] } = company;

  const hasValidCredentialsForProvider = useMemo(
    () => invoiceProviderCredentials.some(
      (credential) => credential.credentialProvider.id === selectedProvider?.id,
    ),
    [selectedProvider, invoiceProviderCredentials],
  );

  const shouldShowCredentialStep = useMemo(() => {
    if (selectedProvider?.name === 'MiPyme' || !selectedProvider?.integrated) {
      return false;
    }
    if (selectedCessionType === 'external') {
      return false;
    }
    return !hasValidCredentialsForProvider;
  }, [selectedProvider, selectedCessionType, hasValidCredentialsForProvider]);

  const shouldShowUploadDocumentsStep = useMemo(() => {
    if (selectedCessionType === 'external') {
      return false;
    }
    return !selectedProvider?.integrated && !allDocumentsHaveXML;
  }, [selectedProvider, selectedCessionType, allDocumentsHaveXML]);

  const [certificatePassword, setCertificatePassword] = useState('');

  return {
    hasDigitalCertificate,
    selectedProvider,
    setSelectedProvider,
    selectedCessionType,
    setSelectedCessionType,
    data,
    loading,
    invoices,
    allDocumentsHaveXML,
    shouldShowCredentialStep,
    shouldShowUploadDocumentsStep,
    certificatePassword,
    setCertificatePassword,
  };
};

export default useOfferCartVariables;
